import React, { useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  fetchBuildingDashboardConfig,
  fetchBuildingDetails,
  fetchBuildingStats,
  fetchCommentSentimentStats,
  IRaffle,
  updateBuilding,
  updateBuildingUnits
} from '~/api/building';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import StatsBox from '~/components/StatsBox/StatsBox';
import { Building, BuildingStats } from '~/types/building';
import { connect } from 'react-redux';
import { RootState } from '~/redux/store';
import { ReactComponent as Bullet } from '~/utils/images/bullet.svg';
import classes from './Dashboard.module.css';
import {
  IBuildingConfigColumn,
  configureDashboardData
} from '~/helpers/configureDashboardData';
import Spinner from '~/components/Spinner/Spinner';
import { Mixpanel } from '../../services/MixpanelService';
import { getBuildingFromStore } from '~/helpers/reduxStoreHelpers';
import { Roles } from '~/enums/Roles';
import { toast } from 'react-toastify';
import { configureCommentSentimentData } from '~/helpers/configureCommentSentimentData';
import { useBuildingKeyAttribute } from '../EditBuildingContainer/EditBuildingKeys/hooks';
import ResourceIcon from '~/utils/images/resources.png';
import Spacer from '~/components/Spacer/Spacer';
import useGetBuildingDetails from '~/hooks/building/useGetBuildingDetails';
import moment from 'moment';
import { useDeeplink } from '~/hooks/deeplinks/useDeeplink';
import { ReactComponent as CopyIcon } from '~/utils/images/copyIcon.svg';
import useRaffle from '~/hooks/building/useRaffle';

type Props = ReturnType<typeof mapStateToProps>;

const Stats = (props: Props) => {
  const { buildingId } = useParams<{ buildingId: string }>();
  const building: null | Partial<Building> = useGetBuildingDetails(buildingId);
  const { raffle } = useRaffle(buildingId);
  const { userRole } = props;
  const queryClient = useQueryClient();
  const { buildingKeyAttributes } = useBuildingKeyAttribute(buildingId);
  const history = useHistory();
  const { activeDeepLinks } = useDeeplink(buildingId);

  const onBoardingDeeplink = useMemo(() => {
    if (!activeDeepLinks?.general) {
      return null;
    }

    return activeDeepLinks?.general.find(
      (deeplink) => deeplink.title === 'Onboarding Link'
    );
  }, [activeDeepLinks?.general]);

  const {
    data: buildingDetailsRes,
    isSuccess: isBuildingDetailsSuccess,
    isLoading: isBuildingDetailsLoading,
    isError: isBuildingDetailsError
  } = useQuery(
    ['buildingDetails', buildingId],
    () => fetchBuildingDetails(buildingId),
    {
      refetchOnWindowFocus: false
    }
  );
  let buildingUserOptions: any = [];
  if (isBuildingDetailsSuccess) {
    const activeResidents = buildingDetailsRes?.data.residents.filter(
      (resident: { deactivated: boolean }) => !resident.deactivated
    );
    buildingUserOptions = activeResidents.map((resident: { email: string }) => {
      return { value: resident.email, label: resident.email };
    });
  }

  const {
    data: buildingDashboardRes,
    isLoading: isBuildingDashboardLoading,
    isError: isBuildingDashboardError
  } = useQuery(
    ['buildingDashboardConfig', buildingId],
    () => fetchBuildingDashboardConfig(buildingId),
    {
      refetchOnWindowFocus: false
    }
  );

  const showSentimentData = () => {
    return (
      (userRole === Roles.Pm &&
        buildingDashboardRes?.data.config.showSentimentDataToPM) ||
      userRole === Roles.Vcm ||
      userRole === Roles.Superadmin
    );
  };

  const {
    data: statsRes,
    isLoading: isBuildingStatsLoading,
    isError: isBuildingStatsError
  } = useQuery(
    ['buildingStats', buildingId],
    () => fetchBuildingStats(buildingId),
    {
      refetchOnWindowFocus: false
    }
  );
  const stats = statsRes?.data;

  const commentSentimentQuery = useQuery(
    ['commentSentiment', buildingId],
    () => fetchCommentSentimentStats(buildingId),
    {
      refetchOnWindowFocus: false,
      enabled: !isBuildingDashboardLoading && showSentimentData()
    }
  );

  if (
    isBuildingDetailsError ||
    isBuildingDashboardError ||
    isBuildingStatsError ||
    commentSentimentQuery.isError
  ) {
    toast('Something went wrong. Please try again.');
  }

  const updateStats = async (totalOccupiedUnits: number) => {
    try {
      let resp = await updateBuildingUnits({ totalOccupiedUnits }, buildingId);
      const copyStats = stats;
      queryClient.setQueryData(['buildingStats', buildingId], {
        data: {
          ...copyStats,
          totalOccupiedUnits: resp.data.totalOccupiedUnits
        }
      });
      toast('Occupied units have been updated!');
    } catch (error) {
      console.log(error);
      toast('Something went wrong! Please try again later.');
    }
  };
  useEffect(() => {
    if (building) {
      Mixpanel.track('did_open_building_dashboard', {
        $building_name: (building as Building).name
      });
    }
  });

  const changeStats = (email: string) => {
    let copyStats: BuildingStats = { ...stats };
    if (userRole === Roles.Superadmin || userRole === Roles.Vcm)
      copyStats.VCMCommenter = email;
    else copyStats.PMCommenter = email;

    queryClient.setQueryData(['buildingStats', buildingId], {
      data: copyStats
    });
  };

  const renderConfigColumn = (data: IBuildingConfigColumn) => {
    const siblingContent = data.sibling ? (
      <div className='margin-bottom-16'>
        {data.sibling.map((e) => renderConfigColumn(e))}
      </div>
    ) : null;

    return (
      <div key={data.title} className={`${classes.column}  margin-right-40`}>
        {!data.appendSiblingAtBottom && siblingContent}
        <div className={classes.subtitleContainer}>
          <Bullet fill={data.color} />
          <div className={classes.subtitle}>{data.title}</div>
        </div>
        {Object.values(data.cards)
          .filter(
            (card) =>
              card.selector !== 'neighborhoodOffers' ||
              buildingDashboardRes?.data.config.showPerks
          )
          .map((card) => {
            return (
              <StatsBox
                key={card.selector}
                title={card.title}
                description={card.description}
                statsValue={card.statsValue}
                color={data.color}
                footerLinkText={card.footerLinkText}
                footerLink={card.footerLink}
                renderCustomFooter={card.renderCustomFooter}
                titleLink={card.titleLink}
                action={card.action}
                building={buildingDetailsRes?.data}
                currentUserRole={userRole}
                changeStats={changeStats}
                show={card.show}
                showActions={card.showAction}
                buildingUserOptions={buildingUserOptions}
                updateOccupiedUnits={updateStats}
                styles={card.styles}
              />
            );
          })}
        {data.appendSiblingAtBottom && siblingContent}
      </div>
    );
  };

  const googleDriveLink = useMemo(() => {
    if (!buildingKeyAttributes) {
      return null;
    }

    const googleDriveAttribute =
      buildingKeyAttributes?.attributedBuildingKeyValue.find((atttribute) =>
        atttribute.name.includes('Google Drive')
      );

    if (googleDriveAttribute) {
      return googleDriveAttribute.value;
    }

    return null;
  }, [buildingKeyAttributes]);

  const isOnboardingCompleted = useMemo(() => {
    if (!buildingKeyAttributes) {
      return true;
    }

    const googleDriveAttribute =
      buildingKeyAttributes?.attributedBuildingKeyValue.find((atttribute) =>
        atttribute.name.includes('Property Onboarded')
      );

    if (googleDriveAttribute) {
      return googleDriveAttribute.value === '1';
    }

    return false;
  }, [buildingKeyAttributes]);

  const onOnboarding = () => {
    history.push(`/building/${buildingId}/on-boarding`);
  };

  const showJoinAppMessage =
    raffle?.amount &&
    raffle?.endDate &&
    buildingDetailsRes?.data.buildingGoLive &&
    moment(new Date()).diff(buildingDetailsRes.data.buildingGoLive, 'days') <=
      30 &&
    moment(new Date()).diff(buildingDetailsRes.data.buildingGoLive, 'days') >=
      0;

  const copyJumpStartLink = () => {
    navigator.clipboard.writeText(
      `${
        showJoinAppMessage
          ? `Join Cobu & win $${
              raffle?.amount
            }! Post your fav restaurant by ${moment(raffle?.endDate).format(
              'M/DD'
            )} 🎉`
          : `Connect with your neighbors on Cobu! A community app made for ${
              (building as unknown as Building).name
            }}`
      }
${onBoardingDeeplink?.link}`
    );

    toast('Link copied to clipboard!');
  };

  const showGoogleDriveAnchor =
    userRole === Roles.Vcm || userRole === Roles.Superadmin;

  return (
    <div>
      {showGoogleDriveAnchor && !isOnboardingCompleted && (
        <div>
          <div className={classes.onBoardingContainer}>
            <div className={classes.onBoardingRightContent}>
              <div className={`bold ${classes.onBoardingTitle}`}>
                ONBOARDING IN PROGRESS...
              </div>
              <a
                href={
                  'https://docs.google.com/spreadsheets/d/12KFsM7-jtXndPY5CW_SCyL2WIbjTWbcnwVMaNtNbqdM/edit?gid=0#gid=0'
                }
                target='_blank'
                rel='noopener noreferrer'
              >
                <button className='btn-standard btn-primary'>
                  View Status
                </button>
              </a>
            </div>
            <button
              onClick={onOnboarding}
              className={`btn-standard btn-secondary ${classes.completeOnBoarding}`}
            >
              Complete Onboarding
            </button>
          </div>
          <Spacer height={30} />
        </div>
      )}
      {!isBuildingDashboardLoading &&
      !isBuildingDetailsLoading &&
      !isBuildingStatsLoading &&
      !commentSentimentQuery.isLoading ? (
        <div className='content-container'>
          <div className={classes.boxContainer}>
            <div className={classes.boxContent}>
              <div className={classes.jumpStartContainer}>
                <div>
                  <div className={classes.jumpStartHeading}>
                      Jump-Start Your Community's Engagement!
                  </div>
                  <div className={classes.jumpStartSubHeading}>
                    Sending an SMS message or email to all of your residents can quickly
                    jump-start your community’s engagement
                  </div>
                </div>
                <div className={classes.jumpStartLinkContainer}>
                  <div
                    className={`${classes.copyJumpStart} cursor-pointer`}
                    onClick={copyJumpStartLink}
                  >
                    <CopyIcon width={20} height={20} />
                    {` Copy`}
                  </div>
                  <div className={classes.jumpStartLink}>
                    <div>
                      {showJoinAppMessage
                        ? `Join Cobu & win $${
                            raffle?.amount
                          }! Post your fav restaurant by ${moment(
                            raffle?.endDate
                          ).format('M/DD')} 🎉`
                        : `Connect with your neighbors on Cobu! A community app made for ${
                            (building as unknown as Building).name
                          }`}
                    </div>
                    <div>{onBoardingDeeplink?.link}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Breadcrumbs />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-end'
            }}
          >
            <div className='margin-top-24'>
              <h2>
                {building ? (building as Building).name : ''}
                {''}
              </h2>
            </div>
            {showGoogleDriveAnchor && googleDriveLink && (
              <div className='margin-right-40'>
                <a href={googleDriveLink} target='_blank'>
                  <img src={ResourceIcon} alt='Google Drive Link' />
                </a>
              </div>
            )}
          </div>
          {showSentimentData() && (
            <div className={`?${classes.subtitleContainer} margin-top-32`}>
              <div className={classes.subtitle}>SENTIMENT VIEWS</div>
            </div>
          )}
          <div>
            <div>
              <div className={classes.container}>
                {commentSentimentQuery.isSuccess &&
                  configureCommentSentimentData(
                    commentSentimentQuery.data.data,
                    buildingId
                  ).map((data) => (
                    <div
                      key={data.title}
                      className={`${classes.column}  margin-right-40`}
                    >
                      <StatsBox
                        title={data.title}
                        description='All comments in 30 days'
                        statsValue={data.statsValue}
                        color={data.color}
                        footerLinkText='See Comments'
                        building={building as unknown as Building}
                        currentUserRole={userRole}
                        show={showSentimentData()}
                        isSentiment
                        footerLink={data.footerLink}
                      />
                    </div>
                  ))}
              </div>
            </div>
            <div className={classes.container}>
              {stats &&
                buildingDashboardRes &&
                configureDashboardData(
                  stats,
                  buildingDetailsRes?.data,
                  userRole,
                  buildingDashboardRes?.data.config
                ).map(renderConfigColumn)}
            </div>
          </div>
        </div>
      ) : (
        <div className={classes.spinner}>
          <Spinner />
        </div>
      )}
    </div>
  );
};

function mapStateToProps(state: RootState) {
  return {
    userRole: state.auth.userRole
  };
}

export default connect(mapStateToProps)(Stats);
